import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { TablesInsert, TablesUpdate } from '@/types/database';

export function useStaff() {
  return useQuery({
    queryKey: ['staff'],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase.from('staff_clinance').select('*').order('name');
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useStaffAssignments(staffId: string) {
  return useQuery({
    queryKey: ['staff_assignments', staffId],
    staleTime: 1000 * 60 * 2,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('staff_clinance_access')
        .select('client_id')
        .eq('staff_id', staffId);
      if (error) throw error;
      return (data ?? []).map((r) => r.client_id);
    },
  });
}

export function useStaffMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['staff'] });

  const create = useMutation({
    mutationFn: async (payload: TablesInsert<'staff_clinance'>) => {
      // 1. Convida via Edge Function (cria auth.users + profile + envia email)
      const { data: inviteData, error: inviteErr } = await supabase.functions.invoke(
        'invite-staff',
        {
          body: { email: payload.email, name: payload.name, role: payload.role },
        },
      );
      if (inviteErr) {
        const msg = (inviteData as { error?: string } | null)?.error ?? inviteErr.message;
        throw new Error(msg);
      }
      if ((inviteData as { error?: string } | null)?.error) {
        throw new Error((inviteData as { error: string }).error);
      }

      // 2. Cria registro em staff_clinance
      const { data, error } = await supabase
        .from('staff_clinance')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Colaborador criado e convite enviado por e-mail');
      invalidate();
    },
    onError: (e: Error) => toast.error(`Erro ao criar colaborador: ${e.message}`),
  });

  const update = useMutation({
    mutationFn: async ({ id, ...payload }: TablesUpdate<'staff_clinance'> & { id: string }) => {
      const { data, error } = await supabase
        .from('staff_clinance')
        .update(payload)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Colaborador atualizado');
      invalidate();
    },
    onError: () => toast.error('Erro ao atualizar colaborador'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('staff_clinance').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Colaborador removido');
      invalidate();
    },
    onError: () => toast.error('Erro ao remover colaborador'),
  });

  const saveAssignments = useMutation({
    mutationFn: async ({ staffId, clientIds }: { staffId: string; clientIds: string[] }) => {
      // Busca assignments atuais
      const { data: current } = await supabase
        .from('staff_clinance_access')
        .select('id, client_id')
        .eq('staff_id', staffId);

      const currentIds = (current ?? []).map((r) => r.client_id);
      const toAdd = clientIds.filter((id) => !currentIds.includes(id));
      const toRemove = (current ?? [])
        .filter((r) => !clientIds.includes(r.client_id))
        .map((r) => r.id);

      if (toRemove.length > 0) {
        const { error } = await supabase.from('staff_clinance_access').delete().in('id', toRemove);
        if (error) throw error;
      }
      if (toAdd.length > 0) {
        const { error } = await supabase
          .from('staff_clinance_access')
          .insert(toAdd.map((client_id) => ({ staff_id: staffId, client_id })));
        if (error) throw error;
      }
    },
    onSuccess: (_, { staffId }) => {
      toast.success('Atribuições salvas');
      qc.invalidateQueries({ queryKey: ['staff_assignments', staffId] });
      qc.invalidateQueries({ queryKey: ['client_options'] });
    },
    onError: () => toast.error('Erro ao salvar atribuições'),
  });

  return { create, update, remove, saveAssignments };
}
